/**
 * @packageDocumentation
 * @module      CloudApi.ts
 * @description Module to access the Cloud API that provides data for this application.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
import Fetcher from "./Fetcher";
import System from "./System";
import LoggedInUser from "./LoggedInUser";
import SystemEx from "./SystemEx";
import RoboticNetworkSummary from "./RoboticNetworkSummary";
import RoboticNetwork from "./RoboticNetwork";
import UserDetails from "./UserDetails";
import AuditLogLine from "./AuditLogLine";
import RoboticNetworkDto from "./RoboticNetworkDto";
import UserDto from "./UserDto";
import CloudApiErrorEvent from "./CloudApiErrorEvent";
/**
 * Module to access the Cloud API that provides data for this application.
 */
export default class CloudApi {
    /**
     * Constructor.
     *
     * @param fetcher An object that mimics JavaScript's fetch function, or null to use fetch directly.
     *                For unit tests, use the Fetcher from this application.
     * @param element The element from which any errors should be dispatched. If undefined then error events will
     *                not be dispatched.
     */
    constructor(fetcher, element = undefined) {
        this.myFetcher = fetcher || new Fetcher();
        this.element = element;
    }
    getHeaders() {
        return {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + LoggedInUser.jwt
        };
    }
    /**
     * Make an RPC-style call. A good result returns a fulfilled Promise with nothing in it.
     * A bad result returns a rejected Promise with a reason. The server method must return the rejected
     * result in the body.
     *
     * @private
     */
    bareCall(httpVerb, url, body) {
        return this.myFetcher.fetch(url, {
            method: httpVerb,
            headers: this.getHeaders(),
            body: body
        }).then((response) => {
            if (response == null) {
                return Promise.reject('There was no response from the server.');
            }
            else if (response.ok) {
                return undefined; // OK
            }
            else {
                return response.text().then((txt) => {
                    return Promise.reject(txt || 'Unknown error.');
                });
            }
        }, (error) => {
            return Promise.reject('The server could not be reached.'); // unsuccessful call because of network failure
        });
    }
    getItems(operation, partialUrl, itemFromJSON) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}`;
        const emptyList = [];
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return r.map((json) => itemFromJSON(json));
                }, (error) => {
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return emptyList; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return emptyList; // response was not ok
            }
        }, (error) => {
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return emptyList; // unsuccessful call because of network failure
        });
    }
    /**
     * Get one item
     *
     * @param itemId The primary key of the item in the database.
     * @param operation A description of the operation being performed. This should fit naturally to fill in the
     * blank of this sentence: "There was an error while attempting to ____: etc."
     * @param partialUrl Something like "/api/v1/roboticNetwork"
     * @param itemFromJSON A function to construct a TItem from the JSON that the server returns.
     * @private
     */
    getItem(itemId, operation, partialUrl, itemFromJSON) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}/${encodeURIComponent(itemId)}`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                if (response.headers.get('Content-Length') === "0") {
                    return undefined; // Example: No associated system in call to getAssociatedSystem
                }
                return response.json().then(r => {
                    return itemFromJSON(r);
                }, (error) => {
                    console.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
        }, (error) => {
            console.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    saveItem(item, operation, partialUrl, httpVerb) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}`;
        return this.myFetcher.fetch(url, {
            method: httpVerb || 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(item)
        }).then((response) => {
            if (!response.ok) {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
            return response;
        }, (error) => {
            console.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Delete an item.
     *
     * @param itemId The primary key of the item to delete.
     * @param operation A description of the operation being performed. This should fit naturally to fill in the
     * blank of this sentence: "There was an error while attempting to ____: etc."
     * @param partialUrl Something like "/api/v1/roboticNetwork"
     */
    deleteItem(itemId, operation, partialUrl) {
        const url = `${appConfig.sessionManagerBase}${partialUrl}/${encodeURIComponent(itemId.toString())}`;
        return this.myFetcher.fetch(url, {
            method: 'DELETE',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (!response.ok) {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was not ok
            }
            return response;
        }, (error) => {
            console.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Get all systems from the database. Does not include devices.
     */
    getSystems() {
        return this.getItems("retrieve all systems", '/api/v1/systems', System.fromJSON);
    }
    /**
     * Get a system, its devices, and its user.
     */
    getSystemEx(systemId) {
        const operation = 'retrieve the system';
        const url = `${appConfig.sessionManagerBase}/api/v1/systemEx/${encodeURIComponent(systemId)}`;
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders(),
            body: undefined
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return SystemEx.fromJSON(r);
                }, (error) => {
                    console.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response was null or not ok
            }
        }, (error) => {
            console.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Store a new extended system object.
     *
     * @param systemEx an encapsulation of a system, its devices, and its dedicated user.
     */
    createSystemEx(systemExDto) {
        return this.saveSystemEx(systemExDto, 'POST');
    }
    /**
     * Update an extended system object.
     *
     * @param systemEx an encapsulation of a system, its devices, and its dedicated user.
     */
    updateSystemEx(systemExDto) {
        return this.saveSystemEx(systemExDto, 'PUT');
    }
    saveSystemEx(systemExDto, httpVerb) {
        return this.saveItem(systemExDto, 'save the system', '/api/v1/systemEx', httpVerb);
    }
    /**
     * Get the system associated with the one identified by systemId, or return undefined if that system is not in
     * an association. The association may be pending, in the process of being rejected, etc.
     *
     * @param systemId The System ID of the system whose associated system you want to get.
     */
    getAssociatedSystem(systemId) {
        return this.getItem(systemId, "retrieve an associated system", '/api/v1/system/associated', System.fromJSON);
    }
    /**
     * Delete a system, cascade-deleting its devices and dedicated user.
     *
     * @param systemId The System ID of the system to delete.
     */
    deleteSystem(systemId) {
        return this.deleteItem(systemId, 'delete the system', '/api/v1/system');
    }
    getRoboticNetworkSummaries() {
        return this.getItems('retrieve all robotic networks', '/api/v1/roboticNetworkSummaries', RoboticNetworkSummary.fromJSON);
    }
    getRoboticNetworkSummariesForSystem(systemId) {
        const partialUrl = `/api/v1/roboticNetworkSummaries/systemId/${encodeURIComponent(systemId)}`;
        return this.getItems('retrieve summary data for robotic networks', partialUrl, RoboticNetworkSummary.fromJSON);
    }
    getRoboticNetwork(roboticNetworkId) {
        return this.getItem(roboticNetworkId, "retrieve the robotic network", '/api/v1/roboticNetwork', RoboticNetwork.fromJSON);
    }
    saveRoboticNetwork(roboticNetwork) {
        return this.saveItem(new RoboticNetworkDto(roboticNetwork), 'save the robotic network', '/api/v1/roboticNetwork');
    }
    /**
     * Delete a robotic network.
     *
     * @param roboticNetworkId The ID of the robotic network to delete.
     */
    deleteRoboticNetwork(roboticNetworkId) {
        return this.deleteItem(roboticNetworkId, 'delete the robotic network', '/api/v1/roboticNetwork');
    }
    getUser(userId) {
        return this.getItem(userId, "retrieve the user", '/api/v1/user', UserDetails.fromJSON);
    }
    getUserByUsername(username) {
        return this.getItem(username, "retrieve user " + username, '/api/v1/username', UserDetails.fromJSON);
    }
    /**
     * Delete a usr
     * @param userId The ID of the user to delete.
     */
    deleteUser(userId) {
        return this.deleteItem(userId, 'delete the user', '/api/v1/user');
    }
    /**
     * Save a user, either creating one if the userId is not present or updating one if it is.
     */
    saveUser(user) {
        return this.saveItem(new UserDto(user), 'save the user', '/api/v1/user', user.userId ? 'PUT' : 'POST');
    }
    /**
     * Get all users from the database.
     */
    getUsers() {
        return this.getItems('retrieve all users', '/api/v1/users', UserDetails.fromJSON);
    }
    /**
     * Request a password reset
     * @param username The username (which must be an email address) to which the request will be sent.
     * @returns On success: a resolved promise. On failure: a rejected promise with a string error reason.
     */
    requestPasswordReset(username) {
        const url = `${appConfig.sessionManagerBase}/api/v1/passwordReset/request?username=${encodeURIComponent(username)}`;
        return this.bareCall('GET', url);
    }
    /**
     * Process a password reset
     * @param username The username whose password is being reset.
     * @param key The key that was sent to the user as part of the password-reset link.
     * @param newPassword The user's new password, which is assumed to have been validated already.
     */
    processPasswordReset(username, key, newPassword) {
        const url = `${appConfig.sessionManagerBase}/api/v1/passwordReset/reset?username=${encodeURIComponent(username)}&key=${encodeURIComponent(key)}&pwd=${encodeURIComponent(newPassword)}`;
        return this.bareCall('POST', url);
    }
    /**
     * Change the user's password.
     * @param username The username.
     * @param oldPassword The old password.
     * @param newPassword The new password.
     */
    changePassword(username, oldPassword, newPassword) {
        const url = `${appConfig.sessionManagerBase}/api/v1/passwordReset/change`;
        const body = JSON.stringify({
            username: username,
            oldPassword: oldPassword,
            newPassword: newPassword
        });
        return this.bareCall('POST', url, body);
    }
    generateNewPassword(element) {
        const url = `${appConfig.sessionManagerBase}/api/v1/newPassword`;
        const operation = "generate a new password";
        return this.myFetcher.fetch(url, {
            method: 'GET',
            headers: this.getHeaders()
        }).then((response) => {
            if (response.ok) {
                return response.json().then(r => {
                    return r.password;
                }, (error) => {
                    console.log(error);
                    CloudApiErrorEvent.dispatch(this.element, operation, response, error);
                    return undefined; // Response was not JSON
                });
            }
            else {
                CloudApiErrorEvent.dispatch(this.element, operation, response);
                return undefined; // response not ok
            }
        }, (error) => {
            console.log(error);
            CloudApiErrorEvent.dispatch(this.element, operation, null, error);
            return undefined; // unsuccessful call because of network failure
        });
    }
    /**
     * Get an audit log.
     *
     * @param auditLogType The type of log.
     */
    getAuditLog(auditLogType) {
        const url = '/api/v1/auditLog/' + auditLogType;
        return this.getItems('retrieve the audit log', url, AuditLogLine.fromJSON);
    }
}
//# sourceMappingURL=CloudApi.js.map